import { use, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { todayDate } from '../../../common/src/date_string.ts';
import { defaultPlaylistName, OrgId, SongListType } from '../../../common/src/model.ts';
import { isBound } from '../../../common/src/model_util.ts';
import { parseSongSlug } from '../../../common/src/paths.ts';
import { localStorageGet, LocalStorageKey, localStorageSet } from '../data/client_local_storage.ts';
import { useCustomMusic } from '../data/use_custom_music.tsx';
import { HymnalsContext } from '../data/use_hymnals.tsx';
import { generateHymnUrlFromSlug, getPageFromLocation } from '../util/path.ts';
import { getSongSlugFromUrl, songSlugSearchParamName } from '../util/shared.tsx';

export function useSetSongIslandPlaylist() {
  const { search } = useLocation();
  const { registerAdditionalOrgIds, unregisterAdditionalOrgIds } = useCustomMusic(); // cannot trigger a refresh

  // only necessary for old URL formats that do not contain the hymnal slug
  const allHymnals = use(HymnalsContext);

  useEffect(() => {
    if (allHymnals.length === 0) {
      return;
    }
    const songSlug = getSongSlugFromUrl(allHymnals, search);
    if (!songSlug) {
      return;
    }
    localStorageSet(LocalStorageKey.IsPlayerMaximized, true);
    if (!new URLSearchParams(search).has(songSlugSearchParamName)) {
      const page = getPageFromLocation(window.location);
      const hymnUrl = generateHymnUrlFromSlug({ songSlug, page });
      history.replaceState(history.state, document.title, hymnUrl);
    }
    const playlist = localStorageGet(LocalStorageKey.Playlist);
    const playlistIndex = localStorageGet(LocalStorageKey.PlaylistIndex);
    const songIndex = playlist?.songs.findIndex((entry) => isBound(entry) && entry.slug === songSlug) ?? -1;
    if (playlistIndex === songIndex) {
      return;
    }
    if (songIndex !== -1) {
      console.log(`Song island updating playlist index to ${songIndex} for ${songSlug}`);
      localStorageSet(LocalStorageKey.PlaylistIndex, songIndex);
      return;
    }
    console.log(`Song island setting playlist for single song ${songSlug}`);
    localStorageSet(LocalStorageKey.Playlist, {
      type: SongListType.Playlist,
      name: defaultPlaylistName,
      date: todayDate(),
      songs: [{ row: 1, slug: songSlug }],
    });
    localStorageSet(LocalStorageKey.PlaylistIndex, 0);

    // IMPORTANT
    // This must only trigger on URL (search) updates, not on any other updates, especially not on playlist updates.
    // We had a race condition where the playlist update triggered this useEffect, but the URL had not yet been
    // updated, so we stomped on the new playlist with the song from the old URL. :(
  }, [allHymnals, search]);

  useEffect(() => {
    const songSlug = getSongSlugFromUrl([], search);
    if (songSlug === undefined || songSlug === '') {
      return;
    }
    let orgIds: OrgId[] = [];
    try {
      const { orgId } = parseSongSlug(songSlug);
      if (orgId) {
        console.log(`Including additional org id ${orgId} for song slug ${songSlug}`);
        orgIds = [orgId];
      }
    } catch (e: any) {
      console.log(`Error parsing song slug ${songSlug}: ${e.message}`);
    }
    registerAdditionalOrgIds(orgIds);
    return () => unregisterAdditionalOrgIds(orgIds);
  }, [registerAdditionalOrgIds, search, unregisterAdditionalOrgIds]);
}
